import React from 'react'
import { Link } from 'react-router-dom'

import TextEditor from '../components/TextEditor/TextEditor'
import Pattern from '../components/Pattern/Pattern'

const doc = {
  title: '.PAT File Syntax & Rules',
  slug: 'pat-file-syntax-and-rules',
  tags: ['education'],
  category: 'pat-file-basics',
  content: (props) => {
      return <div>
      <p className="">
        .PAT files consist of three main parts:
      </p>
      <ul className="list-bulleted">
        <li>
          <i className="fal fa-angle-right fa-fw"/> <strong>file prologue</strong>: establishes the standards and version of this file
        </li>
        <li>
          <i className="fal fa-angle-right fa-fw"/> <strong>pattern prologue</strong>: contains metadata about a pattern, like its name
        </li>
        <li>
          <i className="fal fa-angle-right fa-fw"/> <strong>pattern definition</strong>: tells the software how to draw the lines that form the pattern
        </li>
      </ul>
      <br/>
      <div className="row doc-row row-mega-padded">
        <div className="col-md-6">
          <h5>File Prologue</h5>
          <p>
            Exclusively used by Revit, this section of the .PAT file declares the <code>UNITS</code> (INCH or MM) of each measure and <code>VERSION</code>. The current .PAT file standard is version 3.0, learn more about the history of .PAT files <Link to="/docs/d/what-is-a-pat-file">here</Link>.
          </p>
          <p>
            Both lines need to start with <code>;%</code> and use capital letters.
          </p>
        </div>

        <div className="col-md-6">
          <pre>
<strong>;%UNITS=INCH<br/>
;%VERSION=3.0</strong><br/>
<span className="translucent">;<br/>
*untitled<br/>
;%TYPE=MODEL<br/>
;<br/>
;angle, x,      y,      shift,  offset, dash,   space<br/>
0,      0,      0,      19,     -1,     13,     -47<br/>
90,     3,      0,      19,     1,      13,     -47<br/></span></pre>
        </div>
      </div>


      <div className="row doc-row row-mega-padded">
        <div className="col-md-6">
          <h5>Pattern Prologue</h5>
          <p>
            All .PAT files have at least one line in this section, starting with <code>*</code>, to declare the name of the pattern. AutoCAD requires this name matches the name of the actual file.
          </p>
          <p>
            Revit also requires the declaration of a <code>TYPE</code>. 
          </p>
          <ul className="list-bulleted">
            <li>
              <i className="fal fa-angle-right fa-fw"/> <code>MODEL</code> patterns cause Revit to scale the units of the pattern with the real-world geometry the drawing represents - frequently used for literal patterns like brick or tile layouts. 
            </li>
            <li>
              <i className="fal fa-angle-right fa-fw"/> <code>DRAFTING</code> patterns cause Revit to scale the units relative to the paper the drawing is being printed on - typically used for representational patterns like steel, concrete, or earth.
            </li>
          </ul>
        </div>

        <div className="col-md-6">
          <pre>
<span className="translucent">;%UNITS=INCH<br/>
;%VERSION=3.0<br/>
;</span><br/>
<strong>*untitled<br/>
;%TYPE=MODEL</strong><br/>
<span className="translucent">;<br/>
;angle, x,      y,      shift,  offset, dash,   space<br/>
0,      0,      0,      19,     -1,     13,     -47<br/>
90,     3,      0,      19,     1,      13,     -47<br/></span></pre>
        </div>
      </div>


      <div className="row doc-row row-mega-padded">
        <div className="col-md-6">
          <h5>Pattern Definition</h5>
          <p>
            The most complex part of the .PAT file, this section tells the software how to construct the pattern - line by line. Though not necessary, Pattycake automatically includes a header row to help make the pattern definiton easier to read.
          </p>
          <p>
            Each row in this section defines a line and how it repeats across the pattern. Though many common patterns might appear to repeat in a tile-like fashion, each line actually can repeat in a completely different way. 
          </p>
          <p>
            This section is so important, we've dedicated an entire doc to it called <Link to="/docs/d/deciphering-the-pat-file">Deciphering the .PAT File</Link>.
          </p>
        </div>

        <div className="col-md-6">
          <pre>
<span className="translucent">;%UNITS=INCH<br/>
;%VERSION=3.0<br/>
;<br/>
*untitled<br/>
;%TYPE=MODEL<br/>
;</span><br/>
<strong>;angle, x,      y,      shift,  offset, dash,   space<br/>
0,      0,      0,      19,     -1,     13,     -47<br/>
90,     3,      0,      19,     1,      13,     -47<br/></strong></pre>
        </div>
      </div>

      <h5>Rules</h5>
      <p className="p-push-down">
        While .PAT files are compatible with most CAD software, the most popular two (AutoCAD and Revit) each have their own specific .PAT file requirements. Generally speaking, AutoCAD is much more strict than Revit - even though Revit introduces .PAT file types (MODEL vs. DRAFTING) and has specifications for both. An easy way to remember this is that Revit has fewer rules and more options because it was developed more recently than AutoCAD and doesn't have the same backwards-compatibility requirements of an AutoCAD standard.
      </p>

      <div className="doc-callout doc-callout-warning">
        <span className="doc-callout-type-label"><i className="fas fa-exclamation-circle"/> IMPORTANT NOTE</span>
        <strong>Rules vs Guidelines?</strong>
        <p className="p-push-down">
          Unfortunately, in addition to rules Revit introduces some of its requirements as "guidelines". Non-compliance with these guidelines doesn't necessarily mean the file won't work - but it <i>might</i> not work. To prevent confusion, we recommend treating Revit's guidelines as strict rules to prevent wasting time trying to fix lines that may or may not be the cause of a failed import. Whether or not Revit decides to allow a pattern that breaks a "guideline" is amigious and according to Autodesk, based on the density of the pattern.
        </p>
      </div>

      <h5>Breakdown of Rules (and Guidelines) for AutoCAD & Revit</h5>
      <table className="table">
        <thead>
          <tr>
            <th>Rule</th>
            <th className="text-center">Revit</th>
            <th className="text-center">AutoCAD</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>All lines must be less than 80 characters long</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>Maximum of 6 components to a line pattern</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>No spaces in a pattern name</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>Only one pattern per .PAT file</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>.PAT file name must exactly match the pattern name</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>Pattern name must begin with a letter or number (not a special character)</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>Allows only numbers, letters, underscores, hyphens, and dollar signs in .PAT definition</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>Blank lines and text to the right of a semicolon are ignored</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>


          <tr>
            <td>Blank line must be placed after the last line in the PAT definition</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>Pattern names can’t exceed 31 characters in length</td>
            <td className="text-center translucent">-</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
          </tr>

          <tr>
            <td>All lines must be less than 4096 characters in length</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>Lines beginning with ;% are Revit directives.</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>Lines beginning with ; are comments</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>
  
          <tr>
            <td>Lines beginning with * begin new pattern definitions</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>Blank lines are ignored</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>Name may contain any ASCII characters except for a comma</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>Unlimited number of components per line (for complex dash patterns)</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>Unlimited number of patterns per .PAT file</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>DRAFTING patterns <strong>usually</strong> contain values from .04 to 1.0 inches (1-25 mm)</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

          <tr>
            <td>MODEL patterns <strong>usually</strong> contain values from 2 to 20 inches (50-500 mm)</td>
            <td className="text-center"><i className="fas fa-check fa-fw"/></td>
            <td className="text-center translucent">-</td>
          </tr>

        </tbody>
      </table>
    </div>
  }
}

export default doc;


// WEBPACK FOOTER //
// ./src/docs/pat-file-syntax-and-rules.js